import { useStoreActions, useStoreState } from '@/stores/store';
import { useCallback, useState } from 'react';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { TouchableOpacity, View } from 'react-native';
import { Text } from '@/components/Text';
import {
  ColorOption,
  COLOR_MAP,
  DEFAULT_PROFILE_COLOR,
} from '@/components/ColorPicker';
import { ClampableText } from '@/components/ClampableText';
import { deletePatient } from '@/api/services/patient';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { Icon } from '@/components/Icon';
import { logError } from '@/utils/logHandling';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { Tag } from '@/components/ui/therapist/Tag';
import { Patient } from '@/stores/store.types';
import colors from '@/config/colors';
import moment from 'moment';
import { getInitials } from '@/utils/stringHelpers';
import { styles } from './styles';

type PatientProfileHeaderProps = {
  _id: string;
  fullName: string;
  biography: string;
  diagnosis: string;
  code: string;
  gender: string;
  created: string;
  therapistTags?: Patient['therapistTags'];
  fetchPatients: (
    selectedPatient: Patient | null,
    patientsParameter: Patient[] | null,
  ) => Promise<void>;
  profileColor: ColorOption | '';
  navigation: StackNavigationProp<TherapistStackParamList>;
};

function formatCreatedDate(dateString: string): string {
  const date = moment(dateString);
  if (!date.isValid()) {
    return '';
  }
  return `erstellt am ${date.format('DD.MM.YYYY')}`;
}

export const PatientProfileHeader = ({
  _id,
  fullName,
  biography,
  diagnosis,
  code,
  gender,
  created,
  therapistTags,
  fetchPatients,
  profileColor,
  navigation,
}: PatientProfileHeaderProps) => {
  const { isTherapistDemo } = useStoreState((store) => ({
    isTherapistDemo: store.isTherapistDemo,
  }));
  const { setGlobalPopupState } = useStoreActions((actions) => ({
    setGlobalPopupState: actions.setGlobalPopupState,
  }));

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isInfoCardExpanded, setIsInfoCardExpanded] = useState(false);
  const [isDiagnosisTruncated, setIsDiagnosisTruncated] = useState(false);
  const [isBiographyTruncated, setIsBiographyTruncated] = useState(false);

  const canExpand = isDiagnosisTruncated || isBiographyTruncated;
  const hasCode = !!code;
  const createdLabel = created ? formatCreatedDate(created) : '';
  const resolvedProfileColor = (profileColor ||
    DEFAULT_PROFILE_COLOR) as ColorOption;
  const mappedProfileColor = COLOR_MAP[resolvedProfileColor];

  const handleDeleteClick = useCallback(async () => {
    try {
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        setShowDeleteModal(true);
      } else {
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      }
    } catch (error) {
      logError('handleDeleteClick in PatientProfileHeader', error);
    }
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await deletePatient(_id);
      setShowDeleteModal(false);
      await fetchPatients(null, null);
      navigation.navigate('Home');
    } catch (error) {
      logError('Could not delete patient', error);
    }
  }, [_id, fetchPatients, navigation]);

  const handleEditClick = useCallback(async () => {
    try {
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        navigation.push('AddPatientScreen', { _id });
      } else {
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      }
    } catch (error) {
      logError('Could not edit patient without internet connection', error);
    }
  }, [_id]);

  return (
    <>
      <Modal
        isVisible={showDeleteModal}
        cancelActionButton={
          <Button
            testID="patient-profile-delete-modal-cancel-button"
            label="Abbrechen"
            look="secondary"
            onPress={handleDeleteCancel}
          />
        }
        confirmActionButton={
          <Button
            testID="patient-profile-delete-modal-confirm-button"
            label="Löschen"
            look="danger"
            onPress={handleDeleteConfirm}
          />
        }
        onClose={() => setShowDeleteModal(false)}
        title="Patienten-Profil löschen"
      >
        <Text style={{ fontSize: 20 }}>
          Möchten Sie das Patienten-Profil {fullName} wirklich löschen? Das
          Löschen ist unwiderruflich.
        </Text>
      </Modal>

      <View testID="patient-profile-header" style={styles.container}>
        <View style={styles.topRow}>
          <View style={styles.nameWrapper}>
            <View
              style={[
                styles.profile,
                {
                  backgroundColor: resolvedProfileColor,
                },
              ]}
            >
              <Text style={[styles.profileName]} androidTrailingSpace={false}>
                {getInitials(fullName)}
              </Text>
            </View>
            <View style={styles.nameColumn}>
              <Text
                style={styles.nameText}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {fullName}
              </Text>
              <View style={styles.metaRow}>
                {!!gender && (
                  <>
                    <Text style={styles.metaText}>
                      {gender.length > 8 ? `${gender.slice(0, 8)}…` : gender}
                    </Text>
                    <Text style={styles.metaSeparator}> | </Text>
                  </>
                )}
                <View style={styles.codeRow}>
                  <View
                    style={[
                      styles.codeDot,
                      {
                        backgroundColor: hasCode
                          ? colors.newUI.therapist.brandGreen
                          : colors.newUI.therapist.red50,
                      },
                    ]}
                  />
                  <Text style={styles.metaText}>
                    {hasCode ? code : 'Kein Patienten-Code'}
                  </Text>
                </View>
              </View>
              {!!createdLabel && (
                <Text style={styles.createdText}>{createdLabel}</Text>
              )}
            </View>
          </View>

          {!isTherapistDemo && (
            <View style={styles.actions}>
              <Button
                testID="patient-profile-edit-button"
                look="tertiary"
                label="Bearbeiten"
                icon="edit"
                onPress={handleEditClick}
              />
              <Button
                testID="patient-profile-delete-button"
                look="dangerTertiary"
                label="Löschen"
                icon="trashAlt"
                onPress={handleDeleteClick}
              />
            </View>
          )}
        </View>

        {!!therapistTags?.length && (
          <View testID="patient-profile-tags-row" style={styles.tagsRow}>
            {therapistTags.map((tag) => (
              <Tag key={tag._id} label={tag.name} style={styles.tag} />
            ))}
          </View>
        )}

        {(!!biography || !!diagnosis) && (
          <>
            <View style={styles.divider} />
            {!!diagnosis && (
              <View style={styles.notesSection}>
                <Text style={styles.notesSectionTitle}>Diagnose</Text>
                <ClampableText
                  numberOfLines={1}
                  expanded={isInfoCardExpanded}
                  style={styles.notesText}
                  onTruncatedChange={setIsDiagnosisTruncated}
                >
                  {diagnosis}
                </ClampableText>
              </View>
            )}
            {!!biography && (
              <View style={styles.notesSection}>
                <Text style={styles.notesSectionTitle}>Notizen</Text>
                <ClampableText
                  numberOfLines={2}
                  expanded={isInfoCardExpanded}
                  style={styles.notesText}
                  onTruncatedChange={setIsBiographyTruncated}
                >
                  {biography}
                </ClampableText>
              </View>
            )}
            {canExpand ? (
              <TouchableOpacity
                style={styles.expandButton}
                onPress={() => setIsInfoCardExpanded((prev) => !prev)}
              >
                <Text style={styles.expandButtonText}>
                  {isInfoCardExpanded ? 'einklappen' : 'ausklappen'}
                </Text>
                <Icon
                  name={isInfoCardExpanded ? 'chevronUp' : 'chevronDown'}
                  scale={0.8}
                  color={colors.newUI.therapist.primaryBlue60}
                />
              </TouchableOpacity>
            ) : null}
          </>
        )}
      </View>
    </>
  );
};
